import classNames from 'classnames'
import { motion } from 'framer-motion'

import { SecondsSinceTick } from '@apps-common/components/common'
import { VertSpacerMd, VertSpacerXl } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { GRID_CARD_VARIANTS } from '@apps-common/config/motion-variants'
import { getReceiverPathIcon } from '@apps-common/config/receivers'
import { sortNumerically } from '@apps-common/helpers/array-sort'
import { IGatewayCompany } from '@apps-common/types/portal/cloud-receiver'

export const ReceiverCompanyCardSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div key={`receiver-list-${index}`} className='relative bg-surface-2 w-full rounded-3xl px-4 py-3 overflow-hidden animate-pulse'>
      <div className='absolute top-0 left-0 px-4 py-1 rounded-br-3xl bg-surface-2 w-[120px] h-[30px]' />
      <VertSpacerXl />
      <div className='flex flex-col gap-3'>
        {Array.from(Array(2).keys()).map((gci) => (
          <div key={`receiver-list-item-${gci}`}>
            <div className='rounded-3xl '>
              <div className='text-[0.85em] px-4 pt-2 flex items-center justify-between gap-[5px]'>
                <div />
                <div className='flex items-center justify-end gap-[5px]'>
                  <div className='flex items-center justify-start gap-[3px] bg-secondary-content/10 px-[7px] py-[2px] rounded-xl'>
                    <div className={classNames('w-[12px] h-[12px] rounded-[6px] bg-secondary-content/15')} />
                    <span className='bg-secondary-content/15 w-[15px] h-[10px] rounded' />
                  </div>
                  <div className='flex items-center justify-start gap-[3px] bg-secondary-content/10 px-[7px] py-[2px] rounded-xl'>
                    <div className={classNames('w-[12px] h-[12px] rounded-[6px] bg-secondary-content/15')} />
                    <span className='bg-secondary-content/15 w-[15px] h-[10px] rounded' />
                  </div>
                </div>
              </div>
              <VertSpacerMd />
              <div className='px-3 pb-3 flex flex-wrap gap-[10px]'>
                {Array.from(Array(2).keys()).map((p, pi) => (
                  <div
                    key={`receiver-list-item-path-${pi}`}
                    className='w-full sm:w-auto text-[0.9em] bg-secondary-content/10 rounded-3xl flex items-center justify-between gap-[20px] px-2 py-1'
                  >
                    <div className='flex items-center justify-center gap-[10px]'>
                      <div>
                        <div className='w-[20px] h-[20px] rounded-full bg-secondary-content/15' />
                      </div>
                      <div className='w-[150px] sm:w-[80px] h-[18px] rounded-full bg-secondary-content/15' />
                    </div>
                    <div>
                      <div />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface IProps {
  index: number
  id: string
  name: string
  companies: IGatewayCompany[]
}

export const ReceiverCompanyCard: React.FC<IProps> = ({ index, id, name, companies }) => {
  const UpIcon = commonIcons.receiverOnline
  const DownIcon = commonIcons.receiverOffline

  return (
    <motion.div
      key={`receiver-list-${id}-${index}`}
      variants={GRID_CARD_VARIANTS}
      initial='initial'
      animate='animate'
      className='relative bg-surface-2 md:bg-surface border border-border w-full rounded-3xl md:px-4 md:py-3 overflow-hidden'
    >
      <div className='absolute top-0 left-0 jost-medium px-4 py-1 rounded-br-3xl text-[0.9em] border-b border-r border-border bg-surface md:bg-surface-2'>
        <span>{name}</span>
      </div>
      <VertSpacerXl />
      <div className='flex flex-col gap-3'>
        {companies.map((gc, gci) => {
          const someOffline = gc.company.paths.some((cp) => !cp.online)

          return (
            <div key={`receiver-list-item-${id}-${index}-${gc.id}-${gc.gateway}-${gci}`}>
              <div className='bg-surface-2 rounded-3xl '>
                <div className='text-[0.85em] px-4 pt-3 flex flex-col lg:flex-row items-start justify-between gap-3 lg:gap-[5px]'>
                  <div className='flex flex-col items-start justify-between gap-[5px] lg:bg-surface lg:px-3 lg:py-1 rounded-full lg:border border-border'>
                    <div className='pl-[3px]'>{gc.gateway}</div>
                  </div>
                  <div className='flex items-center justify-between gap-[5px] w-full lg:w-auto'>
                    <div className='flex items-center justify-start'>
                      <div className='flex items-center justify-betwwen gap-[3px] bg-surface px-[8px] min-w-[50px] rounded-xl'>
                        <div
                          className={classNames(
                            'w-[12px] h-[12px] rounded-[6px] border border-border',
                            gc.company.surgardOnline && 'bg-success',
                            !gc.company.surgardOnline && 'bg-error'
                          )}
                        />
                        <span className='text-[0.8em] jost-medium'>In</span>
                      </div>
                      <div className='flex items-center justify-between gap-[3px] bg-surface px-[8px] min-w-[50px] rounded-xl border border-border'>
                        <div
                          className={classNames(
                            'w-[12px] h-[12px] rounded-[6px]',
                            !someOffline && gc.company.companyOnline && 'bg-success',
                            someOffline && gc.company.companyOnline && 'bg-warning',
                            !gc.company.companyOnline && 'bg-error'
                          )}
                        />
                        <span className='text-[0.8em] jost-medium'>Out</span>
                      </div>
                      <div className='text-[0.75rem] flex items-center justify-between gap-[5px] rounded-xl bg-surface px-[8px] min-w-[50px] border border-border'>
                        <commonIcons.stack /> {gc.company.queueSize}
                      </div>
                    </div>
                    <SecondsSinceTick
                      id={`receiver-company-card-seconds-since-tick-${id}-${index}-${gc.id}-${gc.gateway}-${gci}`}
                      timestamp={gc.timestamp}
                      frequency={gc.frequency}
                      className='px-2 rounded-full border'
                      normalClassName='bg-success/60 text-on-success border-success'
                      warningClassName='bg-warning/60 text-on-warning border-warning'
                      warningAfterSeconds={55}
                      dangerClassName='bg-error/60 text-on-error border-error'
                      dangerAfterSeconds={90}
                    />
                  </div>
                </div>
                <VertSpacerMd />
                <div className='px-3 pb-3 flex flex-wrap gap-[10px]'>
                  {gc?.company?.paths
                    ?.sort((a, b) => sortNumerically(a, b, 'pathNo'))
                    .map((p, pi) => {
                      const Icon = getReceiverPathIcon(pi)

                      return (
                        <div
                          key={`receiver-list-item-path-${id}-${index}-${gc.id}-${gc.gateway}-${p.pathIP}-${pi}`}
                          className='w-full sm:w-auto text-[0.9em] bg-surface rounded-3xl flex items-center justify-between gap-[10px] px-2 py-1 border border-border'
                        >
                          <div className='flex items-center justify-center gap-[10px]'>
                            <div>
                              <Icon className='text-[1.5em]' />
                            </div>
                            <div>{p.pathIP?.length > 0 ? p.pathIP : 'None'}</div>
                          </div>
                          <div>
                            {p.online === true ? (
                              <UpIcon className='text-success text-[1.5em]' />
                            ) : (
                              <DownIcon className='text-error text-[1.5em]' />
                            )}
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}
